import React, { useEffect, useState } from 'react';
import { FileText, Shield, Scale, Download, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Card } from '../../new-components/ui/card';
import { Badge } from '../../new-components/ui/badge';
import { Button } from '../../new-components/ui/button';
import MarkdownRenderer from '../../MarkdownRenderer';
import api from '../../../services/api';
import { unwrap } from './shared';
import { SETTINGS_PANEL } from '../ui';

interface LegalDocument {
  id: string;
  document_type: string;
  version: number;
  title: string;
  content_snapshot: string;
  effective_date: string;
  created_at: string;
}

interface ComplianceDoc {
  id: string;
  title: string;
  description: string;
  category: string;
  effectiveDate: string;
  content?: string;
}

type IconType = React.ComponentType<{ className?: string }>;

const TABS = [
  { key: 'signed', label: 'Signed', Icon: FileText },
  { key: 'legal', label: 'Legal', Icon: Scale },
  { key: 'compliance', label: 'Compliance', Icon: Shield },
] as const;

/**
 * Documentation — self-contained Settings section. Owns the legal / compliance / signed
 * document fetch and the sub-tab UI. The section title lives on the SettingsDetail page (<h1>).
 */
export function DocsSection() {
  const [legalDocs, setLegalDocs] = useState<LegalDocument[]>([]);
  const [complianceDocs, setComplianceDocs] = useState<ComplianceDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [docsSubTab, setDocsSubTab] = useState<'signed' | 'legal' | 'compliance'>('signed');
  const [signedDocs, setSignedDocs] = useState<any[]>([]);

  useEffect(() => {
    let ignore = false;
    const fetchDocs = async () => {
      setDocsLoading(true);
      try {
        const [legalRes, complianceRes, signedRes] = await Promise.all([
          api.get('/legal/documents'),
          api.get('/legal/compliance-docs'),
          api.get('/legal/my-signed-docs'),
        ]);
        if (ignore) return;
        setLegalDocs(unwrap(legalRes)?.data || []);
        setComplianceDocs(unwrap(complianceRes)?.data || []);
        setSignedDocs(unwrap(signedRes)?.data || []);
      } catch (err) {
        if (!ignore) console.error('Failed to load documentation:', err);
      } finally {
        if (!ignore) setDocsLoading(false);
      }
    };
    fetchDocs();
    return () => { ignore = true; };
  }, []);

  const toggleDocExpand = (id: string) => {
    setExpandedDoc(prev => (prev === id ? null : id));
  };

  const fmtDate = (d: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  };

  const itemPanel = SETTINGS_PANEL;
  const chevron = (open: boolean) =>
    open ? <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
         : <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />;

  const emptyState = (Icon: IconType, primary: string, secondary?: string) => (
    <div className="py-12 text-center">
      <Icon className="mx-auto mb-3 h-10 w-10 text-muted-foreground" aria-hidden="true" />
      <p className="text-muted-foreground">{primary}</p>
      {secondary && <p className="mt-1 text-xs text-muted-foreground">{secondary}</p>}
    </div>
  );

  return (
    <Card className="p-6 border-border">
      <p className="text-sm text-muted-foreground">
        Legal agreements, compliance policies, and platform documentation.
      </p>

      {/* Sub-tab switcher — full-width equal tabs; icons hide on mobile to fit. */}
      <div className="mt-6 mb-6 grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
        {TABS.map(({ key, label, Icon }) => {
          const active = docsSubTab === key;
          return (
            <button
              key={key}
              onClick={() => setDocsSubTab(key)}
              className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground md:hover:text-foreground'
              }`}
            >
              <Icon className="hidden h-4 w-4 sm:inline-block" aria-hidden="true" />
              {label}
              {key === 'signed' && signedDocs.length > 0 && (
                <span className="rounded-full bg-foreground/10 px-1.5 text-xs font-medium text-foreground">
                  {signedDocs.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {docsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      ) : (
        <>
          {/* Signed Documents */}
          {docsSubTab === 'signed' && (
            <div className="space-y-3">
              {signedDocs.length === 0
                ? emptyState(FileText, 'No signed documents yet.', 'Documents you sign will appear here for your records.')
                : signedDocs.map((doc: any) => {
                    const statusClass =
                      doc.status === 'admitted' || doc.status === 'accepted'
                        ? 'text-[hsl(var(--success))] border-[hsl(var(--success))]/30'
                        : doc.status === 'rejected'
                          ? 'text-destructive border-destructive/30'
                          : 'text-muted-foreground';
                    return (
                      <div key={doc.id} className={`${itemPanel} p-4`}>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">{doc.title}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {doc.type === 'subscription_agreement' ? 'Subscription' : 'E-Signed'}
                              </Badge>
                              {doc.signatureType === 'drawn' && (
                                <Badge variant="outline" className="text-xs text-muted-foreground">Visual Signature</Badge>
                              )}
                              {doc.status && (
                                <Badge variant="outline" className={`text-xs capitalize ${statusClass}`}>
                                  {doc.status}
                                </Badge>
                              )}
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Signed by {doc.signedBy} on {new Date(doc.signedAt).toLocaleDateString()}
                            </p>
                            {doc.amount && (
                              <p className="text-xs text-muted-foreground">
                                Investment: ${(doc.amount / 100).toLocaleString()}
                              </p>
                            )}
                          </div>
                          {doc.downloadUrl && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-shrink-0 self-start"
                              onClick={() => window.open(doc.downloadUrl, '_blank')}
                            >
                              <Download className="mr-1 h-3 w-3" />
                              Download
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
            </div>
          )}

          {/* Legal Agreements */}
          {docsSubTab === 'legal' && (
            <div className="space-y-3">
              {legalDocs.length === 0
                ? emptyState(FileText, 'No legal documents available.')
                : legalDocs.map(doc => {
                    const open = expandedDoc === doc.id;
                    return (
                      <div key={doc.id} className={itemPanel}>
                        <button onClick={() => toggleDocExpand(doc.id)} className="w-full text-left">
                          <div className="flex items-start justify-between gap-3 p-4">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground">{doc.title}</p>
                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                <Badge variant="outline" className="text-xs">v{doc.version}</Badge>
                                <span className="text-xs text-muted-foreground">
                                  Effective {fmtDate(doc.effective_date || doc.created_at)}
                                </span>
                              </div>
                            </div>
                            <div className="mt-0.5">{chevron(open)}</div>
                          </div>
                        </button>
                        {open && (
                          <div className="border-t border-border p-4 rise-in">
                            <div className="max-h-96 overflow-y-auto rounded-lg bg-background p-4">
                              <MarkdownRenderer content={doc.content_snapshot || 'Document content not available.'} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
            </div>
          )}

          {/* Compliance Policies */}
          {docsSubTab === 'compliance' && (
            <div className="space-y-3">
              {complianceDocs.length === 0
                ? emptyState(Shield, 'No compliance documents available.')
                : complianceDocs.map(doc => {
                    const open = expandedDoc === `compliance-${doc.id}`;
                    return (
                      <div key={doc.id} className={itemPanel}>
                        <button onClick={() => toggleDocExpand(`compliance-${doc.id}`)} className="w-full text-left">
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-2">
                              <p className="min-w-0 text-sm font-medium text-foreground">{doc.title}</p>
                              <div className="flex flex-shrink-0 items-center gap-2">
                                <Badge variant="secondary" className="text-xs">{doc.category}</Badge>
                                {chevron(open)}
                              </div>
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">{doc.description}</p>
                            <p className="mt-2 text-xs text-muted-foreground">Effective {fmtDate(doc.effectiveDate)}</p>
                          </div>
                        </button>
                        {open && doc.content && (
                          <div className="border-t border-border p-4 rise-in">
                            <div className="max-h-96 overflow-y-auto rounded-lg bg-background p-4">
                              <MarkdownRenderer content={doc.content} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

              <p className="pt-1 text-xs text-muted-foreground">
                Full compliance documents are maintained by Platform Engineering and are available
                upon request for institutional partners, banking counterparties, and compliance reviewers.
              </p>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export default DocsSection;
